import React, { useState, useEffect, useRef } from "react";
import Helmet from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Formik, Form } from "formik";
import { twoFASetup, twoFASetupVerify } from "store/actions";
import { TwoFACodeValidation } from "./Validation";
import BackupCodesModal from "components/Modals/BackupCodeModel";
import { TWO_FA_SETUP } from "common/viewContent";

const STEP = {
    QR: "QR",
    CODE: "CODE",
};

const OTP_LENGTH = 6;

const OtpDigitInput = ({ value, onChange, error }) => {
    const inputsRef = useRef([]);
    const digits = value ? value.split("") : [];

    const setDigitAt = (index, digit) => {
        const next = [...Array(OTP_LENGTH)].map((_, i) => digits[i] || "");
        next[index] = digit;
        onChange(next.join(""));
    };

    const handleChange = (e, index) => {
        const val = e.target.value.replace(/[^0-9]/g, "");
        if (!val) {
            setDigitAt(index, "");
            return;
        }
        const char = val[val.length - 1];
        setDigitAt(index, char);
        if (index < OTP_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (digits[index]) {
                setDigitAt(index, "");
            } else if (index > 0) {
                inputsRef.current[index - 1]?.focus();
                setDigitAt(index - 1, "");
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            inputsRef.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, OTP_LENGTH);
        if (pasted) {
            onChange(pasted.padEnd(OTP_LENGTH, "").slice(0, OTP_LENGTH).trimEnd());
            const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
            inputsRef.current[focusIndex]?.focus();
        }
    };

    return (
        <div className="mb-3">
            <label className="fw-bold d-block mb-2">2FA Code</label>
            <div className="d-flex" style={{ gap: "10px" }}>
                {[...Array(OTP_LENGTH)].map((_, index) => (
                    <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digits[index] || ""}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className={`form-control text-center${error ? " is-invalid" : ""}`}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: "56px",
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            color: "#1a1a2e",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "8px",
                        }}
                        autoFocus={index === 0}
                    />
                ))}
            </div>
            {error && <div className="text-danger small mt-1">{error}</div>}
        </div>
    );
};

const TwoFASetup = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { twoFASetuploading, twoFASetupVerifyloading } = useSelector((state) => state.Auth);

    const email = location.state?.email || "";
    const password = location.state?.password || "";
    const hasCredentials = Boolean(email && password);

    const [step, setStep] = useState(STEP.QR);
    const [qrData, setQrData] = useState({ qr: "", uri: "" });
    const [apiError, setApiError] = useState("");

    const [backupCodes, setBackupCodes] = useState([]);
    const [showBackupModal, setShowBackupModal] = useState(false);

    const generateQr = async () => {
        if (!hasCredentials) return;
        setApiError("");
        try {
            const res = await dispatch(twoFASetup({ data: { email, password } })).unwrap();
            setQrData({
                qr: res?.qr || "",
                uri: res?.uri || "",
            });
        } catch (err) {
            console.error(err);
            setApiError(err?.message || "Something went wrong. Please try again.");
        }
    };

    useEffect(() => {
        if (hasCredentials) {
            generateQr();
        }
    }, []);

    const handleCodeSubmit = async (values, { setSubmitting }) => {
        setApiError("");
        try {
            const res = await dispatch(
                twoFASetupVerify({ data: { email, twoFACode: values.twoFACode } })
            ).unwrap();

            const codes = res?.backupCodes || res?.data?.backupCodes || [];

            if (codes.length) {
                setBackupCodes(codes);
                setShowBackupModal(true);
            } else {
                navigate("/login");
            }
        } catch (err) {
            console.error(err);
            setApiError(err?.message || "Invalid or expired code. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadBackupCodes = () => {
        const content = [
            "Bitanica Games - 2FA Backup Codes",
            `Account: ${email}`,
            "",
            ...backupCodes,
            "",
            "Keep these codes safe. Each code can be used once.",
        ].join("\n");

        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "backup-codes.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setShowBackupModal(false);
        navigate("/login");
    };

    const handleCloseBackupModal = () => {
        setShowBackupModal(false);
        navigate("/login");
    };

    const stepSubText = {
        [STEP.QR]: "Scan this QR code using your authenticator app",
        [STEP.CODE]: "Please enter the 2FA code from your authenticator app",
    };

    const handleBackClick = () => {
        if (step === STEP.CODE) {
            setStep(STEP.QR);
        } else {
            navigate("/login");
        }
    };

    return (
        <>
            <Helmet title={TWO_FA_SETUP.CURRENT_MODULE} />
            <div className="container mt-5" style={{ overflow: "hidden" }}>
                <div className="row">
                    <div className="col-12 col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4">
                        <div className="login-brand text-center mb-4">
                            <h2 className="mb-1">Welcome</h2>
                            <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                                Bitanica Games
                            </p>
                        </div>

                        <div className="login-brand"></div>
                        <div className="card card-primary">
                            <div className="card-header d-flex align-items-center position-relative">
                                <button
                                    type="button"
                                    onClick={handleBackClick}
                                    className="btn btn-link p-0 position-absolute"
                                    style={{
                                        left: "1rem",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#6c757d",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    aria-label="Back"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5" />
                                        <path d="M12 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <h4 className="w-100 text-center mb-0">
                                    {TWO_FA_SETUP.CURRENT_MODULE}
                                </h4>
                            </div>
                            <div className="card-body text-center">
                                <p className="text-muted mb-1">{stepSubText[step]}</p>

                                {hasCredentials ? (
                                    <p className="text-muted small mb-3">
                                        <strong>{email}</strong>
                                    </p>
                                ) : (
                                    <div className="alert alert-warning py-2 text-left mb-3">
                                        We couldn't find your login details for this session.{" "}
                                        <Link to="/login">Please log in again</Link> to set up 2FA.
                                    </div>
                                )}

                                {apiError && (
                                    <div className="alert alert-danger py-2 text-left">{apiError}</div>
                                )}

                                {step === STEP.QR && (
                                    <div>
                                        <div
                                            className="d-flex align-items-center justify-content-center mb-3"
                                            style={{
                                                width: "200px",
                                                height: "200px",
                                                margin: "0 auto",
                                                border: "1px solid #ced4da",
                                                borderRadius: "8px",
                                                padding: "8px",
                                                background: "#fff",
                                            }}
                                        >
                                            {twoFASetuploading ? (
                                                <span className="text-muted small">Loading QR code...</span>
                                            ) : qrData.qr ? (
                                                <img
                                                    src={qrData.qr}
                                                    alt="2FA QR Code"
                                                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                />
                                            ) : (
                                                <span className="text-muted small">QR code unavailable</span>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm d-block mb-2"
                                            style={{ width: "100%" }}
                                            onClick={generateQr}
                                            disabled={!hasCredentials || twoFASetuploading}
                                        >
                                            {twoFASetuploading ? "Regenerating..." : TWO_FA_SETUP.REGENERATE}
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-primary d-block"
                                            style={{ width: "100%" }}
                                            onClick={() => setStep(STEP.CODE)}
                                            disabled={!qrData.qr}
                                        >
                                            {TWO_FA_SETUP.CONTINUE}
                                        </button>
                                    </div>
                                )}

                                {step === STEP.CODE && (
                                    <div className="text-left">
                                        <Formik
                                            initialValues={{ twoFACode: "" }}
                                            validationSchema={TwoFACodeValidation}
                                            onSubmit={handleCodeSubmit}
                                        >
                                            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                                                <Form>
                                                    <OtpDigitInput
                                                        value={values.twoFACode}
                                                        onChange={(val) => setFieldValue("twoFACode", val)}
                                                        error={touched.twoFACode ? errors.twoFACode : ""}
                                                    />
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary d-block"
                                                        style={{ width: "100%" }}
                                                        disabled={isSubmitting || twoFASetupVerifyloading}
                                                    >
                                                        {TWO_FA_SETUP.SUBMIT}
                                                    </button>
                                                </Form>
                                            )}
                                        </Formik>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="simple-footer text-center mt-4">
                            Copyright &copy; 2024 Bitanica Games. All rights reserved.
                        </div>
                    </div>
                </div>
            </div>

            <BackupCodesModal
                show={showBackupModal}
                handleClose={handleCloseBackupModal}
                codes={backupCodes}
                onDownload={handleDownloadBackupCodes}
            />
        </>
    );
};

export default TwoFASetup;